import { createContext } from 'react';

// True inside the print copy of the program (ProgramPrint): images load at once instead of lazily.
export const PrintContext = createContext(false);

// Pure helpers for the public program (P10, plan §7.6). They only read the answers of
// GET /api/public/events/{slug}/program and /people (heartscms publicapi: PublicProgramView,
// PublicPeopleView); nothing here decides visibility, the backend already did.

export const ROLE_ORDER = ['MODERATOR', 'SPEAKER', 'PANELIST'];
export const BODY_ORDER = ['ORGANIZER', 'SCIENTIFIC_COMMITTEE', 'SCIENTIFIC_ADVISORY_BOARD'];

// Decision 6 of §7.6: titles follow the site language; when a text exists in one language only,
// that one is shown (English is the soft default of a one-language program).
export const pick = (language, de, en) => {
  const hasDe = typeof de === 'string' && de.trim() !== '';
  const hasEn = typeof en === 'string' && en.trim() !== '';
  if (language === 'de' && hasDe) return de;
  if (hasEn) return en;
  return hasDe ? de : '';
};

// "09:00:00" or "09:00" -> "09:00".
export const formatTime = (t) => (t ? String(t).slice(0, 5) : '');

export const timeRange = (start, end) => {
  const s = formatTime(start);
  const e = formatTime(end);
  if (s && e) return `${s}–${e}`;
  return s || '';
};

// A "YYYY-MM-DD" date as a local date (new Date('2026-11-05') would be UTC midnight).
export const localDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

export const isEntry = (node) => node && node.type === 'ENTRY';
export const isElement = (node) => node && node.type === 'DISPLAY_ELEMENT';

// The days that hold something, in the backend's order; nodes without a day (should not happen at the
// top level) go to a trailing pseudo-day so nothing is lost.
export const daysOf = (program) => {
  const days = (program?.days || []).map((d) => ({ ...d }));
  const known = new Set(days.map((d) => d.id));
  const orphans = (program?.entries || []).some((n) => n.dayId == null || !known.has(n.dayId));
  if (orphans) days.push({ id: null, day: null, labelDe: null, labelEn: null });
  return days.filter((d) => (program?.entries || []).some((n) => sameDay(n, d, known)));
};

const sameDay = (node, day, known) => (day.id == null ? node.dayId == null || !known.has(node.dayId) : node.dayId === day.id);

// One day of the timeline:
//   before    top-level display elements placed before every entry of the day
//   slots     timed top-level entries grouped by start time, ordered by time; entries of one slot run
//             in parallel (several rooms) and keep the backend's order (room, then sort order).
//             slot.after: the display elements the admin placed right after an entry of this slot
//   untimed   top-level entries without a start time, grouped per room (decision 2 of §7.6); each
//             item { entry, after } carries the elements placed right after that entry
// Top-level entries and display elements of a day share one sequence (design §4.4.1); the answer gives
// each its `position` in it. A display element goes right after the entry that precedes it there,
// since the timeline itself is ordered by time. Without positions (older answers) elements go first.
export const layoutDay = (program, day) => {
  const known = new Set((program?.days || []).map((d) => d.id));
  const nodes = (program?.entries || []).filter((n) => sameDay(n, day, known));
  const elements = nodes.filter(isElement);
  const entries = nodes.filter(isEntry);

  const after = new Map(); // entry id -> elements placed after it, in sequence order
  const before = [];
  elements
    .slice()
    .sort((a, b) => (a.position ?? -1) - (b.position ?? -1))
    .forEach((el) => {
      let anchor = null;
      if (el.position != null) {
        entries.forEach((e) => {
          if (e.position != null && e.position < el.position && (anchor == null || e.position > anchor.position)) anchor = e;
        });
      }
      if (!anchor) before.push(el);
      else {
        if (!after.has(anchor.id)) after.set(anchor.id, []);
        after.get(anchor.id).push(el);
      }
    });

  const timed = entries.filter((e) => e.startTime);
  const slotMap = new Map();
  timed.forEach((e) => {
    const key = formatTime(e.startTime);
    if (!slotMap.has(key)) slotMap.set(key, []);
    slotMap.get(key).push(e);
  });
  const slots = [...slotMap.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([time, list]) => ({
      time,
      entries: list,
      after: list.flatMap((e) => after.get(e.id) || []).sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    }));

  const untimedMap = new Map();
  entries.filter((e) => !e.startTime).forEach((e) => {
    const key = e.roomId ?? null;
    if (!untimedMap.has(key)) untimedMap.set(key, []);
    untimedMap.get(key).push({ entry: e, after: after.get(e.id) || [] });
  });
  const roomRank = new Map((program?.rooms || []).map((r, i) => [r.id, i]));
  const untimed = [...untimedMap.entries()]
    .sort(([a], [b]) => (a == null ? 1 : b == null ? -1 : (roomRank.get(a) ?? 0) - (roomRank.get(b) ?? 0)))
    .map(([roomId, items]) => ({ roomId, items }));

  return { before, slots, untimed };
};

// The people of one entry for printing: per role (chairs first), and inside a role per group label,
// a heading whenever the label changes (design §4.4.1: country headings of HEARTS MATCH).
export const peopleByRole = (people) => {
  const out = [];
  ROLE_ORDER.concat((people || []).map((p) => p.role).filter((r) => !ROLE_ORDER.includes(r)))
    .filter((role, i, all) => all.indexOf(role) === i)
    .forEach((role) => {
      const ofRole = (people || []).filter((p) => p.role === role);
      if (ofRole.length === 0) return;
      const groups = [];
      ofRole.forEach((p) => {
        const label = p.groupLabel || null;
        const last = groups[groups.length - 1];
        if (last && last.label === label) last.people.push(p);
        else groups.push({ label, people: [p] });
      });
      out.push({ role, count: ofRole.length, groups });
    });
  return out;
};

// The room and, when it is not the event's default venue, the venue of a top-level entry.
export const placeOf = (program, roomId) => {
  if (roomId == null) return { room: null, venue: null };
  const room = (program?.rooms || []).find((r) => r.id === roomId) || null;
  if (!room) return { room: null, venue: null };
  const defaultVenueId = program?.event?.defaultVenueId ?? null;
  const venuesUsed = new Set((program?.rooms || []).map((r) => r.venueId));
  const otherVenue = defaultVenueId != null ? room.venueId !== defaultVenueId : venuesUsed.size > 1;
  const venue = otherVenue ? (program?.venues || []).find((v) => v.id === room.venueId) || null : null;
  return { room, venue };
};

// entry id -> { dayId, topId } for every entry in the tree, to jump to a person's entries.
export const indexEntries = (program) => {
  const index = new Map();
  const walk = (nodes, dayId, topId) => {
    (nodes || []).forEach((n) => {
      if (!isEntry(n)) return;
      const top = topId ?? n.id;
      const day = topId == null ? n.dayId ?? null : dayId;
      index.set(n.id, { dayId: day, topId: top });
      walk(n.children, day, top);
    });
  };
  walk(program?.entries, null, null);
  return index;
};

// The public profile page of a person (/people/:personId). The page itself is not built yet; until it
// is, PUBLIC_PROFILES stays false and the program shows no profile link. Turning it on is this one line.
export const PUBLIC_PROFILES = false;
export const profileUrlFor = (personId) => (PUBLIC_PROFILES && personId != null ? `/people/${personId}` : null);

// Initials for a person without a photo: "Dr. Ada Example, PhD" -> "AE" (titles with a dot dropped).
export const initialsFor = (name) => {
  if (!name) return '?';
  const words = String(name).split(',')[0].trim().split(/\s+/).filter((w) => !w.endsWith('.'));
  if (words.length === 0) return '?';
  const first = words[0][0] || '';
  const last = words.length > 1 ? words[words.length - 1][0] : '';
  return (first + last).toUpperCase();
};
